/**
 * Error Recovery Hook
 * Provides error handling and recovery options
 */
import { useState, useCallback } from 'react';
import { message } from 'antd';

interface RecoveryOption {
  strategy: string;
  action: string;
  description: string;
  execute: () => Promise<any>;
}

export function useErrorRecovery() {
  const [isRecovering, setIsRecovering] = useState(false);
  const [lastError, setLastError] = useState<Error | null>(null);
  
  const handleError = useCallback(async (
    error: Error,
    context?: any
  ): Promise<RecoveryOption[]> => {
    console.error('Error occurred:', error, context);
    setLastError(error);
    
    // Categorize error
    const errorType = categorizeError(error);
    
    // Get recovery options
    const options: RecoveryOption[] = [];
    
    if (errorType === 'network') {
      options.push({
        strategy: 'retry',
        action: 'retry_request',
        description: 'Try again',
        execute: async () => {
          setIsRecovering(true);
          await new Promise(resolve => setTimeout(resolve, 1000));
          setIsRecovering(false);
          return { retry: true };
        },
      });
      
      options.push({
        strategy: 'offline',
        action: 'work_offline',
        description: 'Continue offline',
        execute: async () => {
          message.info('Working in offline mode');
          return { offline: true };
        },
      });
    }
    
    if (errorType === 'permission') {
      options.push({
        strategy: 'request',
        action: 'request_permission',
        description: 'Request access',
        execute: async () => {
          message.info('Permission request sent to administrator');
          return { requested: true };
        },
      });
    }
    
    // Always provide a fallback
    options.push({
      strategy: 'report',
      action: 'report_issue',
      description: 'Report this issue',
      execute: async () => {
        // Would open feedback form
        message.info('Thank you for reporting this issue');
        return { reported: true };
      },
    });
    
    return options;
  }, []);
  
  const categorizeError = (error: Error): string => {
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('fetch')) {
      return 'network';
    }
    if (message.includes('permission') || message.includes('unauthorized')) {
      return 'permission';
    }
    if (message.includes('timeout')) {
      return 'timeout';
    }
    
    return 'unknown';
  };
  
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);
  
  return {
    handleError,
    isRecovering,
    lastError,
    clearError,
  };
}